import { useMemo, useState, useEffect } from "react";

import { ThreeDots as Loader } from "react-loader-spinner";
import { useOrder } from "../../hooks/queries/useOrder";
import Loading from "../../components/Loading";
import { useSendItems } from "../../hooks/mutations/useSendItems";
import { useCancelOrder } from "../../hooks/mutations/useCancelOrder";
import { useCloseOrder } from "../../hooks/mutations/useCloseOrder";
import { useTotals } from "../../hooks/useTotals";
import { numberFormat } from "../../utils/numberFormat";
import { calculateMaxQuantity } from "../../utils/calculateMaxQuantity";

import { setTitle } from "../../services/browser";

import { useAppContext } from "../../contexts/AppContext";

import { ButtonsContainer, LoaderContainer } from "../../styles/common";
import colors from "../../styles/colors";
import {
  Container,
  BtnPrimary,
  Btn,
  BtnContinueShopping,
  OrderContentWrapper,
  LoadingOverlay,
  LoadingText,
  PaymentSection,
  PaymentMethodSelector,
  PaymentMethodLabel,
  PaymentMethodRadio,
  PaymentMethodsContainer,
  PaymentMethodCard,
  PaymentMethodTitle,
} from "./styles";

import BtnCancel from "../../components/BtnCancel";
import Parcelamento from "../../components/Parcelamento";

import OrderItemsList from "../../components/OrderItemsList";
import OrderError, {
  ItemError,
  OrderErrorState,
  ValidationMessage,
} from "./components/OrderError";
import CreditCard from "./components/CreditCard";

import OrderItem from "../../types/OrderItem";
import { CreditCardInfo } from "types/CreditCardInfo";
import { OrderItemToSend } from "../../types/ItemToSend";

const Order = () => {
  // Estado local para itens a serem enviados (substitui Redux)
  const [itemsToSend, setItemsToSend] = useState<OrderItemToSend[]>([]);

  // Obtém estado da campanha solar do Context
  const { isCampanhaSolar } = useAppContext();

  // Busca dados do pedido via React Query
  const {
    data: orderData,
    isLoading: isLoadingOrder,
    isFetching: isFetchingOrder,
  } = useOrder();
  const sendItemsMutation = useSendItems();
  const cancelOrderMutation = useCancelOrder();
  const closeOrderMutation = useCloseOrder();

  // Usa dados do React Query
  const loading = isLoadingOrder;

  // Verifica se está atualizando (mutation pendente ou refetch em andamento)
  const isUpdating =
    sendItemsMutation.isPending ||
    closeOrderMutation.isPending ||
    isFetchingOrder;
  const items = orderData?.items || [];
  const orderNrPedido = orderData?.nr_pedido ? Number(orderData.nr_pedido) : 0;

  // Calcula totais usando hook centralizado
  const finalTotals = useTotals(items);

  const parsedVlrMinimo = parseFloat(
    String(orderData?.clientInfo?.session?.cliente?.vlrMinimo ?? 50)
  );
  const vlrMinimo = Number.isNaN(parsedVlrMinimo) ? 50 : parsedVlrMinimo;

  // Calcula minimumReached usando o total líquido (não o total com impostos)
  const minimumReachedCalculated = useMemo(() => {
    if (items.length === 0) {
      return false;
    }

    return finalTotals.liquidNumber >= vlrMinimo;
  }, [finalTotals.liquidNumber, vlrMinimo, items.length]);

  const hasBloqueioPedido =
    (orderData?.clientInfo?.session?.bloqueio_pedido?.msgs?.length ?? 0) > 0;

  const orderValidationMessages = useMemo((): ValidationMessage[] => {
    const messages: ValidationMessage[] = [];

    if (!minimumReachedCalculated) {
      messages.push({
        id: "valor-minimo",
        message: `Valor mínimo de ${numberFormat(vlrMinimo)} não atingido`,
      });
    }

    return messages;
  }, [minimumReachedCalculated, vlrMinimo]);

  // Estado para armazenar os dados de parcelamento selecionados
  const [parcelamentoData, setParcelamentoData] = useState<{
    codigoParcelamento?: string | undefined;
    codigoParcelamentoEspecial?: string | undefined;
    prazo?: string | undefined;
    qtdDias?: string | undefined;
  }>({
    codigoParcelamento: "",
    codigoParcelamentoEspecial: "",
    prazo: "",
    qtdDias: "",
  });

  const [creditCardData, setCreditCardData] = useState<CreditCardInfo>({
    numero: "",
    titular: "",
    cvv: "",
    validade: "",
    numeroParcelas: "01",
    bandeira: "",
  });

  const [paymentMethod, setPaymentMethod] = useState<"parcelamento" | "cartao">(
    "parcelamento"
  );

  useEffect(() => {
    if (hasBloqueioPedido) {
      setPaymentMethod("cartao");
    }
  }, [hasBloqueioPedido]);

  const canCloseCalculated = useMemo(() => {
    if (!minimumReachedCalculated) {
      return false;
    }

    if (paymentMethod === "cartao") {
      if (!creditCardData.numero.trim()) {
        return false;
      }

      if (hasBloqueioPedido) {
        return true;
      }
    } else if (hasBloqueioPedido) {
      return false;
    }

    const podeFechar =
      orderData?.clientInfo?.session?.bloqueio_pedido?.podeFechar;
    return podeFechar ?? false;
  }, [
    minimumReachedCalculated,
    hasBloqueioPedido,
    paymentMethod,
    creditCardData.numero,
    orderData?.clientInfo?.session?.bloqueio_pedido?.podeFechar,
  ]);

  const [orderError, setOrderError] = useState<OrderErrorState | null>(null);
  /**
   * Cobrança autorizada na adquirente que o R2D2 não conseguiu registrar (errorCode 34).
   *
   * Trava o fechamento do pedido de forma DEFINITIVA nesta sessão: o cartão já foi
   * cobrado, e uma segunda tentativa cobraria de novo. Só atendimento resolve — por isso
   * não há caminho de volta na tela.
   */
  const [cobrancaPendente, setCobrancaPendente] = useState(false);

  useEffect(() => {
    if (closeOrderMutation.error) {
      interface ApiError {
        response?: {
          data?: {
            errorType?: string;
            returnMessage?: string;
            error?: string;
            errorCode?: number;
            itemErro?: ItemError[];
          };
        };
      }
      const apiError = closeOrderMutation.error as ApiError;
      const errorData = apiError?.response?.data;
      const errorType = errorData?.errorType;
      const returnMessage = errorData?.returnMessage || null;
      const errorMsg = errorData?.error || null;
      const errorCode = errorData?.errorCode;
      const itemErro = errorData?.itemErro || [];
      const fallbackMsg = `Erro ao finalizar pedido${errorCode ? ` (Código: ${errorCode})` : ""}`;

      // ERRO_PENDENTE_RECONCILIACAO no backend (SafraPay.ts). Reconhecido pelo CÓDIGO e
      // não pelo texto: a mensagem pode mudar, a consequência não.
      if (errorCode === 34) {
        setCobrancaPendente(true);
      }

      if (errorType === "payment_invalid" || errorType === "payment") {
        if (paymentMethod === "cartao") {
          setOrderError({
            paymentError: {
              error: errorMsg,
              returnMessage: returnMessage,
            },
            itemErrors: itemErro,
          });
        } else {
          setOrderError({
            genericError: errorMsg || fallbackMsg,
            itemErrors: itemErro,
          });
        }
      } else {
        setOrderError({
          genericError: errorMsg || fallbackMsg,
          itemErrors: itemErro,
        });
      }
    } else if (closeOrderMutation.data?.itemErro?.length) {
      setOrderError({
        itemErrors: closeOrderMutation.data.itemErro as ItemError[],
      });
    } else if (!closeOrderMutation.isPending) {
      setOrderError(null);
    }
  }, [
    closeOrderMutation.error,
    closeOrderMutation.data,
    closeOrderMutation.isPending,
    paymentMethod,
  ]);

  const getAddButtonText = () => {
    return !itemsToSend || (itemsToSend && itemsToSend.length === 0)
      ? "Nenhum item para atualizar"
      : `Atualizar ${itemsToSend.length} ite${
          itemsToSend.length > 1 ? "ns" : "m"
        } do pedido`;
  };

  const setQt = (product: OrderItem, value: number) => {
    const maxQtd = calculateMaxQuantity(
      value,
      product.mercadoria.infos,
      product.mercadoria.bloqueio
    );

    const newProduct: OrderItemToSend = {
      cd_mercadoria: product.cd_mercadoria,
      qtd: maxQtd,
      mercadoria: product.mercadoria,
    };

    const updatedItemsToSend = itemsToSend.filter(
      item => product.cd_mercadoria !== item.cd_mercadoria
    );

    updatedItemsToSend.push(newProduct);
    setItemsToSend(updatedItemsToSend);
  };

  const delItem = (
    product: OrderItem,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setQt(product, 0);
    } else {
      const updatedItemsToSend = itemsToSend.filter(
        item => product.cd_mercadoria !== item.cd_mercadoria
      );

      setItemsToSend(updatedItemsToSend);
    }
  };

  const sendItems = async (event: React.FormEvent) => {
    event.preventDefault();

    const arrPrepared: {
      mercadoria: string;
      qtd: number;
      placeholder: number;
    }[] = [];
    itemsToSend.forEach(item => {
      // O placeholder é a quantidade atual no pedido (qt_pedida)
      // Busca o item atual no pedido para pegar a quantidade
      const currentItem = items.find(
        i => i.cd_mercadoria === item.cd_mercadoria
      );
      const currentQty = currentItem?.qt_pedida || 0;
      arrPrepared.push({
        mercadoria: item.mercadoria.full_cd_mercadoria,
        qtd: parseInt(String(item.qtd), 10),
        placeholder: parseInt(String(currentQty), 10) || 0,
      });
    });

    // Reset parcelamento é feito automaticamente pelo hook useParcelamento quando os totais mudam

    // Usa o hook de mutation do React Query
    sendItemsMutation.mutate(
      { items: arrPrepared, sum: false },
      {
        onSuccess: () => {
          // Limpa os itens a serem enviados após sucesso
          setItemsToSend([]);
        },
      }
    );
  };

  const cancelOrder = (event: React.MouseEvent) => {
    event.preventDefault();
    cancelOrderMutation.mutate();
  };

  const closeOrder = (event: React.MouseEvent) => {
    event.preventDefault();

    // Guarda redundante com o `disabled` do botão, de propósito: se a cobrança ficou
    // pendente de reconciliação, nenhum caminho pode disparar uma segunda tentativa.
    if (cobrancaPendente) {
      return;
    }

    setOrderError(null);

    // Prepara os dados baseado no método de pagamento selecionado
    if (paymentMethod === "parcelamento") {
      closeOrderMutation.mutate({
        codigoParcelamento: parcelamentoData.codigoParcelamento || "",
        codigoParcelamentoEspecial:
          parcelamentoData.codigoParcelamentoEspecial || "",
        prazo: parcelamentoData.prazo || "",
        qtdDias: parcelamentoData.qtdDias || "",
      });
    } else {
      closeOrderMutation.mutate({
        codigoParcelamento: "",
        codigoParcelamentoEspecial: "",
        prazo: "",
        qtdDias: "",
        cartao: {
          ...creditCardData,
          numero: creditCardData.numero.replace(/\s/g, ""),
        },
      });
    }
  };

  // Altera o método de pagamento selecionado
  const handlePaymentMethodChange = (method: "parcelamento" | "cartao") => {
    if (method === "parcelamento" && hasBloqueioPedido) {
      return;
    }

    setPaymentMethod(method);
    if (orderError?.paymentError) {
      setOrderError(prev => {
        if (!prev) {
          return null;
        }

        const next: OrderErrorState = {};
        if (prev.genericError) {
          next.genericError = prev.genericError;
        }
        if (prev.itemErrors?.length) {
          next.itemErrors = prev.itemErrors;
        }

        return Object.keys(next).length > 0 ? next : null;
      });
    }
  };

  useEffect(() => {
    setTitle(orderNrPedido > 0 ? `Pedido #${orderNrPedido}` : "Pedido");
  }, [orderNrPedido]);

  if (loading) {
    return (
      <Container>
        <Loading message="Carregando pedido..." />
      </Container>
    );
  }

  return (
    <Container onSubmit={sendItems}>
      <h1>Pedido {orderNrPedido > 0 && `#${orderNrPedido}`}</h1>
      {items.length === 0 ? (
        <p>Nenhum item no seu pedido</p>
      ) : (
        <>
          <OrderContentWrapper $isUpdating={isUpdating}>
            {isUpdating && (
              <LoadingOverlay>
                <Loader color={colors.loader.primary} height={50} width={50} />
                <LoadingText>
                  {closeOrderMutation.isPending
                    ? "Finalizando pedido..."
                    : "Atualizando pedido..."}
                </LoadingText>
              </LoadingOverlay>
            )}
            <OrderError
              paymentMethod={paymentMethod}
              orderError={orderError}
              validationMessages={orderValidationMessages}
              showBloqueioCadastroNotice={hasBloqueioPedido}
            />

            <OrderItemsList
              loading={loading}
              perfumaria={finalTotals.perfumaria}
              medicamentos={finalTotals.medicamentos}
              liquid={finalTotals.liquid}
              total={finalTotals.total}
              minimumReached={minimumReachedCalculated}
              items={items}
              itemsToSend={itemsToSend}
              setQt={setQt}
              delItem={delItem}
            />

            <PaymentSection>
              <PaymentMethodSelector>
                <PaymentMethodLabel $disabled={hasBloqueioPedido}>
                  <PaymentMethodRadio
                    type="radio"
                    name="paymentMethod"
                    value="parcelamento"
                    checked={paymentMethod === "parcelamento"}
                    disabled={hasBloqueioPedido}
                    onChange={() => handlePaymentMethodChange("parcelamento")}
                  />
                  <span>Parcelamento</span>
                </PaymentMethodLabel>
                <PaymentMethodLabel>
                  <PaymentMethodRadio
                    type="radio"
                    name="paymentMethod"
                    value="cartao"
                    checked={paymentMethod === "cartao"}
                    onChange={() => handlePaymentMethodChange("cartao")}
                  />
                  <span>Cartão de Crédito</span>
                </PaymentMethodLabel>
              </PaymentMethodSelector>

              <PaymentMethodsContainer>
                <PaymentMethodCard>
                  <PaymentMethodTitle>Parcelamento</PaymentMethodTitle>
                  <Parcelamento
                    total={finalTotals.total}
                    totalMedicamentos={finalTotals.medicamentos}
                    totalPerfumaria={finalTotals.perfumaria}
                    isCampanhaSolar={isCampanhaSolar}
                    disabled={
                      hasBloqueioPedido || paymentMethod !== "parcelamento"
                    }
                    onParcelamentoChange={setParcelamentoData}
                  />
                </PaymentMethodCard>
                <PaymentMethodCard>
                  <PaymentMethodTitle>Cartão de Crédito</PaymentMethodTitle>
                  <CreditCard
                    setCreditCardData={setCreditCardData}
                    totalWithTax={finalTotals.total}
                    disabled={paymentMethod !== "cartao"}
                  />
                </PaymentMethodCard>
              </PaymentMethodsContainer>
            </PaymentSection>

            <ButtonsContainer>
              <BtnCancel
                disabled={cancelOrderMutation.isPending}
                onClick={cancelOrder}
              >
                {cancelOrderMutation.isPending ? (
                  <LoaderContainer>
                    <Loader
                      color={colors.loader.muted}
                      height={15}
                      width={30}
                    />
                    <span>enviando...</span>
                  </LoaderContainer>
                ) : (
                  "Cancelar pedido"
                )}
              </BtnCancel>
              <BtnContinueShopping to="/">
                Continuar comprando
              </BtnContinueShopping>
              <Btn
                type="submit"
                disabled={
                  !itemsToSend || (itemsToSend && itemsToSend.length === 0)
                }
              >
                {sendItemsMutation.isPending ? (
                  <LoaderContainer>
                    <Loader
                      color={colors.loader.light}
                      height={15}
                      width={30}
                    />
                    <span>enviando...</span>
                  </LoaderContainer>
                ) : (
                  getAddButtonText()
                )}
              </Btn>

              <BtnPrimary
                disabled={
                  closeOrderMutation.isPending ||
                  !canCloseCalculated ||
                  cobrancaPendente
                }
                onClick={closeOrder}
              >
                {closeOrderMutation.isPending ? (
                  <LoaderContainer>
                    <Loader
                      color={colors.loader.light}
                      height={15}
                      width={30}
                    />
                    <span>finalizando...</span>
                  </LoaderContainer>
                ) : (
                  "Finalizar pedido"
                )}
              </BtnPrimary>
            </ButtonsContainer>
          </OrderContentWrapper>
        </>
      )}
    </Container>
  );
};

export default Order;
